/**
 * Every Settings page, its address and where it sits in the menu (CLA-1796).
 *
 * One list feeds the desktop side menu, the phone's list of rows, the ⌘K
 * commands and the redirects from the old one-page anchors, so a page added
 * here is reachable from all four at once.
 */

import type { LucideIcon } from 'lucide-react'
import {
  Bell, CalendarClock, CreditCard, FileText, FlaskConical, Lightbulb, Scale, Shield,
  SlidersHorizontal, TextCursorInput, User, Video, Wand2,
} from 'lucide-react'
import { regionalText } from '@/lib/regional-copy'

export type SettingsGroup = 'You' | 'Notes' | 'Patients reach you' | 'Account'

export interface SettingsPage {
  /** Route path under /settings, and the page's own anchor id. */
  path: string
  id: string
  label: string
  /** One line under the row on the phone list, and the ⌘K subtitle. */
  summary: string
  group: SettingsGroup
  icon: LucideIcon
  /** Extra ⌘K search words. */
  keywords: string[]
  /**
   * Anchors on this page. `/settings#<anchor>` (the old one-page links) lands
   * on this page; the first four are the ids the one-page Settings had.
   */
  anchors: string[]
  /** Platform staff only: the menu item and the route are hidden from everyone else. */
  staffOnly?: boolean
}

export const SETTINGS_GROUPS: SettingsGroup[] = ['You', 'Notes', 'Patients reach you', 'Account']

export const SETTINGS_PAGES: SettingsPage[] = [
  {
    path: 'profile', id: 'profile', label: 'Profile & registrations', group: 'You', icon: User,
    get summary() { return regionalText('Name, role, specialties, licences') },
    keywords: ['profile', 'name', 'title', 'qualifications', 'role', 'practitioner type', 'specialty', 'specialties', 'credentials', 'licence', 'license', 'registration', 'ahpra', 'npi'],
    anchors: ['your-details', 'specialties', 'credentials'],
  },
  {
    path: 'notes/style', id: 'note-style', label: 'Note style', group: 'Notes', icon: FileText,
    summary: 'Templates, format, detail, wording',
    keywords: ['note', 'style', 'template', 'templates', 'profile', 'profiles', 'format', 'detail', 'voice', 'soap', 'quote', 'medicine names', 'abbreviations', 'safety statements', 'instructions', 'live'],
    anchors: ['note-profiles', 'format', 'detail-level', 'voice', 'customise', 'additional-instructions', 'live-note-writing'],
  },
  {
    path: 'notes/added', id: 'added', label: 'Added to every note', group: 'Notes', icon: TextCursorInput,
    summary: 'Consult times, your name, closing text',
    keywords: ['append', 'auto-append', 'closing text', 'consult times', 'sign off', 'signature', 'your name', 'footer'],
    anchors: ['consult-times', 'sign-off', 'closing-text'],
  },
  {
    path: 'notes/learned', id: 'learned', label: 'Learned from your edits', group: 'Notes', icon: Wand2,
    summary: 'Changes Claro makes because you asked twice',
    keywords: ['defaults', 'your defaults', 'redraft', 'learned', 'edits'],
    anchors: ['defaults'],
  },
  {
    path: 'notes/insights', id: 'insights', label: 'Clinical insights', group: 'Notes', icon: Lightbulb,
    summary: 'What Claro prepares after each note',
    keywords: ['insights', 'clinical intelligence', 'after each note', 'follow-up', 'register'],
    anchors: ['after-each-note'],
  },
  {
    path: 'bookings', id: 'bookings', label: 'Hours & booking card', group: 'Patients reach you', icon: CalendarClock,
    summary: 'Working hours, photo and bio',
    keywords: ['hours', 'working hours', 'availability', 'roster', 'booking', 'bio', 'photo'],
    anchors: ['working-hours', 'booking-card'],
  },
  {
    path: 'calls', id: 'calls', label: 'Video calls', group: 'Patients reach you', icon: Video,
    summary: 'Call display name, screening preferences',
    keywords: ['telehealth', 'video', 'call', 'display name', 'screening'],
    anchors: ['call-display-name', 'screening'],
  },
  {
    path: 'notifications', id: 'notifications', label: 'Notifications', group: 'Account', icon: Bell,
    summary: 'Message previews',
    keywords: ['notifications', 'push', 'message previews', 'lock screen'],
    anchors: ['message-previews'],
  },
  {
    path: 'security', id: 'security', label: 'Sign-in & security', group: 'Account', icon: Shield,
    summary: 'Password, passkeys, close account',
    keywords: ['password', 'passkey', 'passkeys', 'security', 'sign in', 'login', 'close account', 'delete account'],
    anchors: ['password', 'passkeys', 'close-account'],
  },
  {
    path: 'plan', id: 'plan', label: 'Plan & invites', group: 'Account', icon: CreditCard,
    summary: 'Your plan, usage and referral link',
    keywords: ['plan', 'billing', 'subscription', 'upgrade', 'invoices', 'referral', 'refer', 'invite'],
    anchors: ['referrals'],
  },
  {
    path: 'preferences', id: 'preferences', label: 'Preferences', group: 'Account', icon: SlidersHorizontal,
    get summary() { return regionalText('Colour, timezone, discarding, sharing aftercare') },
    keywords: ['preferences', 'colour', 'color', 'theme', 'appearance', 'navy', 'green', 'timezone', 'time zone', 'confirm discard', 'aftercare', 'halaxy', 'emr', 'copy notes'],
    anchors: ['colour', 'timezone', 'confirm-discard', 'share-aftercare', 'emr-note-copy'],
  },
  {
    path: 'legal', id: 'legal', label: 'Legal', group: 'Account', icon: Scale,
    summary: 'Agreements you accepted, to read again',
    keywords: ['legal', 'terms', 'agreement', 'agreements', 'accepted', 'msa', 'subscription agreement', 'privacy', 'privacy policy', 'acceptable use', 'dpa', 'baa', 'contract'],
    anchors: [],
  },
  {
    path: 'labs', id: 'labs', label: 'Labs', group: 'Account', icon: FlaskConical,
    summary: 'Staff only: draft comparison, preferred pipeline',
    keywords: ['labs', 'comparison', 'a/b', 'pipeline'],
    anchors: ['draft-comparison', 'preferred-pipeline'],
    staffOnly: true,
  },
]

/** Where `/settings` lands on a wide screen. */
export const SETTINGS_HOME = '/settings/notes/style'

/**
 * Phone width: no side menu. `/settings` is a list of rows and each page has
 * a way back to it, like iOS Settings.
 */
export const SETTINGS_PHONE_QUERY = '(max-width: 640px)'

export function settingsPageById(id: string): SettingsPage | undefined {
  return SETTINGS_PAGES.find((p) => p.id === id)
}

/** The pages this user may see. */
export function visibleSettingsPages(isStaff: boolean): SettingsPage[] {
  return SETTINGS_PAGES.filter((p) => isStaff || !p.staffOnly)
}

/**
 * Where an old `/settings#anchor` link now goes, or null when the anchor is
 * unknown. A page's own id lands at the top of the page; any other anchor
 * keeps its hash so the page scrolls to it.
 */
export function settingsPathForAnchor(anchor: string, isStaff: boolean): string | null {
  for (const page of visibleSettingsPages(isStaff)) {
    if (anchor === page.id) return `/settings/${page.path}`
    if (page.anchors.includes(anchor)) return `/settings/${page.path}#${anchor}`
  }
  return null
}
